/*
 * PKI.X509 — certificate parsing, building and signature verification.
 *
 * Parsing decodes every extension a public web certificate can carry, because
 * "Extension 1.3.6.1.5.5.7.1.24: <hex blob>" is not an answer — the point of a
 * decoder is that the user does not have to know what the blob means.
 *
 * Building covers the self-signed and CA-signed cases. Everything is signed
 * through WebCrypto; the ASN.1 around it is assembled here.
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var Asn1 = P.Asn1 || (typeof require !== 'undefined' ? require('./asn1.js') : null);
    var Oid = P.Oid || (typeof require !== 'undefined' ? require('./oid.js') : null);
    var Keys = P.Keys || (typeof require !== 'undefined' ? require('./keys.js') : null);

    var TAG = Asn1.TAG;

    /* ═══ Name (RDNSequence) ════════════════════════════════════════ */

    function parseName(node) {
        var attrs = [];
        (node.children || []).forEach(function (rdn) {
            (rdn.children || []).forEach(function (atv) {
                if (!atv.children || atv.children.length < 2) return;
                var typeOid = atv.children[0].oid;
                var valueNode = atv.children[1];
                attrs.push({
                    oid: typeOid,
                    short: Oid.rdnShort(typeOid),
                    label: (Oid.RDN[typeOid] && Oid.RDN[typeOid].label) || Oid.label(typeOid),
                    value: valueNode.text !== undefined ? valueNode.text :
                        (valueNode.oid || Asn1.toHex(valueNode.bytes)),
                    valueTag: valueNode.tag
                });
            });
        });
        return {
            attributes: attrs,
            der: node.full,
            // RFC 4514 renders most-specific-first; openssl renders the opposite.
            // Both appear in the wild and people match against whichever their
            // own tooling prints, so we carry both rather than pick a winner.
            rfc4514: attrs.slice().reverse().map(function (a) {
                return a.short + '=' + escapeRfc4514(a.value);
            }).join(','),
            openssl: attrs.map(function (a) { return '/' + a.short + '=' + a.value; }).join(''),
            get: function (shortName) {
                var hit = attrs.filter(function (a) { return a.short === shortName; });
                return hit.length ? hit[0].value : null;
            },
            all: function (shortName) {
                return attrs.filter(function (a) { return a.short === shortName; })
                    .map(function (a) { return a.value; });
            }
        };
    }

    function escapeRfc4514(v) {
        return String(v)
            .replace(/([\\+",;<>])/g, '\\$1')
            .replace(/^ /, '\\ ').replace(/ $/, '\\ ')
            .replace(/^#/, '\\#');
    }

    /** Build a Name from [{oid|short, value}] — order is preserved as given. */
    function buildName(attrs) {
        var rdns = attrs.filter(function (a) {
            return a && a.value !== undefined && a.value !== null && String(a.value).length;
        }).map(function (a) {
            var oid = a.oid || shortToOid(a.short);
            if (!oid) throw new Error('unknown DN attribute: ' + (a.short || ''));
            var value;
            if (oid === '1.2.840.113549.1.9.1' || oid === '0.9.2342.19200300.100.1.25') {
                value = Asn1.ia5String(String(a.value));        // emailAddress / DC are IA5
            } else if (oid === '2.5.4.6') {
                value = Asn1.printableString(String(a.value).toUpperCase()); // country: 2-letter
            } else {
                value = Asn1.directoryString(String(a.value));
            }
            return Asn1.set(Asn1.sequence(Asn1.oid(oid), value));
        });
        return Asn1.sequence(rdns);
    }

    var SHORT_TO_OID = null;
    function shortToOid(shortName) {
        if (!SHORT_TO_OID) {
            SHORT_TO_OID = {};
            Object.keys(Oid.RDN).forEach(function (k) { SHORT_TO_OID[Oid.RDN[k].short] = k; });
        }
        return SHORT_TO_OID[shortName] || null;
    }

    /* ═══ GeneralName ═══════════════════════════════════════════════ */

    var GN_KIND = ['otherName', 'email', 'dns', 'x400', 'directory', 'ediParty', 'uri', 'ip', 'registeredID'];

    function parseGeneralNames(node) {
        var out = [];
        (node.children || []).forEach(function (gn) {
            var kind = GN_KIND[gn.tagNumber] || ('tag' + gn.tagNumber);
            var value;
            switch (gn.tagNumber) {
                case 1: case 2: case 6:
                    value = Asn1.decodeString(TAG.IA5_STRING, gn.bytes); break;
                case 7:
                    value = formatIp(gn.bytes); break;
                case 8:
                    value = Asn1.decodeOid(gn.bytes); break;
                case 4:
                    // directoryName is EXPLICIT-tagged: the Name sits inside.
                    value = gn.children && gn.children[0] ? parseName(gn.children[0]).rfc4514 : '';
                    break;
                case 0:
                    value = parseOtherName(gn); break;
                default:
                    value = Asn1.toHex(gn.bytes);
            }
            out.push({ kind: kind, value: value, tagNumber: gn.tagNumber });
        });
        return out;
    }

    function parseOtherName(gn) {
        // Most common otherName by far is the Microsoft UPN on client certs.
        if (!gn.children || !gn.children.length) return Asn1.toHex(gn.bytes);
        var typeOid = gn.children[0].oid;
        var valNode = gn.children[1] && gn.children[1].children && gn.children[1].children[0];
        var text = valNode && valNode.text !== undefined ? valNode.text : '';
        if (typeOid === '1.3.6.1.4.1.311.20.2.3') return 'UPN:' + text;
        return Oid.label(typeOid) + ':' + (text || Asn1.toHex(gn.bytes));
    }

    function formatIp(bytes) {
        var b = Asn1.u8(bytes);
        if (b.length === 4) return b.join('.');
        if (b.length === 16) {
            var parts = [];
            for (var i = 0; i < 16; i += 2) parts.push(((b[i] << 8) | b[i + 1]).toString(16));
            return compressIpv6(parts);
        }
        // 8 or 32 bytes = address + mask, which appears in name constraints.
        if (b.length === 8) return b.subarray(0, 4).join('.') + '/' + maskToPrefix(b.subarray(4));
        if (b.length === 32) return formatIp(b.subarray(0, 16)) + '/' + maskToPrefix(b.subarray(16));
        return Asn1.toHex(b, ':');
    }

    function maskToPrefix(mask) {
        var bits = 0;
        for (var i = 0; i < mask.length; i++) {
            var v = mask[i];
            while (v & 0x80) { bits++; v = (v << 1) & 0xff; }
        }
        return bits;
    }

    function compressIpv6(parts) {
        var best = -1, bestLen = 0, cur = -1, curLen = 0;
        for (var i = 0; i < parts.length; i++) {
            if (parts[i] === '0') {
                if (cur < 0) { cur = i; curLen = 0; }
                curLen++;
                if (curLen > bestLen) { best = cur; bestLen = curLen; }
            } else { cur = -1; curLen = 0; }
        }
        if (bestLen < 2) return parts.join(':');
        return parts.slice(0, best).join(':') + '::' + parts.slice(best + bestLen).join(':');
    }

    function buildGeneralName(entry) {
        switch (entry.kind) {
            case 'dns':   return Asn1.context(2, new TextEncoder().encode(entry.value), false);
            case 'email': return Asn1.context(1, new TextEncoder().encode(entry.value), false);
            case 'uri':   return Asn1.context(6, new TextEncoder().encode(entry.value), false);
            case 'ip':    return Asn1.context(7, parseIpToBytes(entry.value), false);
            default: throw new Error('unsupported GeneralName kind: ' + entry.kind);
        }
    }

    function parseIpToBytes(text) {
        var s = String(text).trim();
        if (s.indexOf(':') >= 0) {
            // IPv6, including the :: shorthand.
            var halves = s.split('::');
            var head = halves[0] ? halves[0].split(':') : [];
            var tail = halves.length > 1 && halves[1] ? halves[1].split(':') : [];
            var fill = 8 - head.length - tail.length;
            if (halves.length > 1) {
                head = head.concat(new Array(Math.max(0, fill)).fill('0'));
            }
            var words = head.concat(tail);
            if (words.length !== 8) throw new Error('bad IPv6: ' + s);
            var out6 = new Uint8Array(16);
            words.forEach(function (w, i) {
                var v = parseInt(w || '0', 16);
                out6[i * 2] = v >> 8; out6[i * 2 + 1] = v & 0xff;
            });
            return out6;
        }
        var octets = s.split('.').map(Number);
        if (octets.length !== 4 || octets.some(function (o) { return !(o >= 0 && o <= 255); })) {
            throw new Error('bad IPv4: ' + s);
        }
        return new Uint8Array(octets);
    }

    /* ═══ extensions ════════════════════════════════════════════════ */

    var KEY_USAGE_BITS = ['digitalSignature', 'nonRepudiation', 'keyEncipherment', 'dataEncipherment',
        'keyAgreement', 'keyCertSign', 'cRLSign', 'encipherOnly', 'decipherOnly'];

    function parseExtensions(extsNode) {
        var out = [];
        (extsNode.children || []).forEach(function (ext) {
            if (!ext.children || ext.children.length < 2) return;
            var oid = ext.children[0].oid;
            var critical = false, valueIdx = 1;
            if (ext.children[1].tag === TAG.BOOLEAN) { critical = ext.children[1].bool; valueIdx = 2; }
            var raw = ext.children[valueIdx] ? ext.children[valueIdx].bytes : new Uint8Array(0);
            var e = { oid: oid, name: Oid.label(oid), critical: critical, raw: raw, value: null };
            try { e.value = decodeExtension(oid, raw); } catch (err) { e.decodeError = err.message; }
            out.push(e);
        });
        return out;
    }

    function decodeExtension(oid, raw) {
        var node;
        switch (oid) {
            case '2.5.29.17': // subjectAltName
            case '2.5.29.18': // issuerAltName
                return parseGeneralNames(Asn1.parse(raw));

            case '2.5.29.19': { // basicConstraints
                node = Asn1.parse(raw);
                var ca = false, pathLen = null;
                (node.children || []).forEach(function (c) {
                    if (c.tag === TAG.BOOLEAN) ca = c.bool;
                    else if (c.tag === TAG.INTEGER) pathLen = intOf(c);
                });
                return { ca: ca, pathLen: pathLen };
            }

            case '2.5.29.15': { // keyUsage
                node = Asn1.parse(raw);
                var bits = node.bits, usages = [];
                for (var i = 0; i < KEY_USAGE_BITS.length; i++) {
                    var byte = bits[Math.floor(i / 8)];
                    if (byte !== undefined && (byte & (0x80 >> (i % 8)))) usages.push(KEY_USAGE_BITS[i]);
                }
                return usages;
            }

            case '2.5.29.37': // extKeyUsage
                return (Asn1.parse(raw).children || []).map(function (c) {
                    return { oid: c.oid, name: Oid.label(c.oid) };
                });

            case '2.5.29.14': // subjectKeyIdentifier
                return Asn1.toHex(Asn1.parse(raw).bytes, ':').toUpperCase();

            case '2.5.29.35': { // authorityKeyIdentifier
                node = Asn1.parse(raw);
                var aki = { keyId: null, issuer: null, serial: null };
                (node.children || []).forEach(function (c) {
                    if (c.tagNumber === 0) aki.keyId = Asn1.toHex(c.bytes, ':').toUpperCase();
                    else if (c.tagNumber === 1) aki.issuer = parseGeneralNames(c);
                    else if (c.tagNumber === 2) aki.serial = Asn1.toHex(c.bytes).replace(/^0+/, '') || '0';
                });
                return aki;
            }

            case '1.3.6.1.5.5.7.1.1':   // authorityInfoAccess
            case '1.3.6.1.5.5.7.1.11': { // subjectInfoAccess
                node = Asn1.parse(raw);
                return (node.children || []).map(function (ad) {
                    var method = ad.children[0].oid;
                    var loc = ad.children[1];
                    return {
                        method: method,
                        methodName: Oid.ACCESS[method] || Oid.label(method),
                        location: loc.tagNumber === 6 ? Asn1.decodeString(TAG.IA5_STRING, loc.bytes) : Asn1.toHex(loc.bytes)
                    };
                });
            }

            case '2.5.29.31': { // cRLDistributionPoints
                node = Asn1.parse(raw);
                var urls = [];
                (node.children || []).forEach(function (dp) {
                    walk(dp, function (n) {
                        if (n.cls === 2 && n.tagNumber === 6 && !n.constructed) {
                            urls.push(Asn1.decodeString(TAG.IA5_STRING, n.bytes));
                        }
                    });
                });
                return urls;
            }

            case '2.5.29.32': { // certificatePolicies
                node = Asn1.parse(raw);
                return (node.children || []).map(function (pi) {
                    var pOid = pi.children[0].oid;
                    var known = Oid.POLICY[pOid];
                    var qualifiers = [];
                    if (pi.children[1]) {
                        walk(pi.children[1], function (n) {
                            if (n.tag === TAG.IA5_STRING) qualifiers.push(n.text);
                            else if (n.tag === TAG.UTF8_STRING || n.tag === TAG.VISIBLE_STRING) qualifiers.push(n.text);
                        });
                    }
                    return {
                        oid: pOid,
                        name: known ? known.label : Oid.label(pOid),
                        level: known ? known.level : null,
                        qualifiers: qualifiers
                    };
                });
            }

            case '2.5.29.30': { // nameConstraints
                node = Asn1.parse(raw);
                var nc = { permitted: [], excluded: [] };
                (node.children || []).forEach(function (sub) {
                    var target = sub.tagNumber === 0 ? nc.permitted : nc.excluded;
                    (sub.children || []).forEach(function (subtree) {
                        var gn = subtree.children && subtree.children[0];
                        if (gn) target.push(parseGeneralNames({ children: [gn] })[0]);
                    });
                });
                return nc;
            }

            case '1.3.6.1.5.5.7.1.24': { // TLS Feature — must-staple lives here
                node = Asn1.parse(raw);
                var features = (node.children || []).map(intOf);
                return { features: features, mustStaple: features.indexOf(5) >= 0 };
            }

            case '1.3.6.1.4.1.11129.2.4.2': // SCT list
                return parseSctList(raw);

            case '2.16.840.1.113730.1.13': // Netscape comment
                return Asn1.parse(raw).text || '';

            default: {
                // Unknown: still try to show something structured rather than hex.
                try {
                    var n = Asn1.parse(raw);
                    if (n.text !== undefined) return n.text;
                    if (n.oid) return n.oid;
                } catch (e) { /* not ASN.1 — fall through to hex */ }
                return null;
            }
        }
    }

    /**
     * Certificate Transparency SCTs. Not ASN.1 inside — the OCTET STRING holds
     * a TLS-style length-prefixed list, which is why generic decoders print it
     * as a hex blob. Knowing the log count and timestamps is the whole point:
     * a public cert with zero SCTs will be rejected by Chrome.
     */
    function parseSctList(raw) {
        var inner = Asn1.parse(raw).bytes; // OCTET STRING wrapping the list
        var dv = new DataView(inner.buffer, inner.byteOffset, inner.byteLength);
        if (inner.length < 2) return [];
        var listLen = dv.getUint16(0), off = 2, out = [];
        var end = Math.min(2 + listLen, inner.length);
        while (off + 2 <= end) {
            var sctLen = dv.getUint16(off); off += 2;
            if (off + sctLen > end || sctLen < 43) break;
            var version = inner[off];
            var logId = inner.subarray(off + 1, off + 33);
            var tsHi = dv.getUint32(off + 33), tsLo = dv.getUint32(off + 37);
            var ts = tsHi * 4294967296 + tsLo;
            out.push({
                version: version,
                logId: Asn1.toHex(logId),
                logIdBase64: (P.Pem || root.PKI.Pem).toBase64(logId),
                timestamp: new Date(ts)
            });
            off += sctLen;
        }
        return out;
    }

    function walk(node, fn) {
        fn(node);
        if (node.children) node.children.forEach(function (c) { walk(c, fn); });
    }

    function intOf(node) {
        var b = node.bytes, v = 0;
        for (var i = 0; i < b.length; i++) v = v * 256 + b[i];
        return v;
    }

    /* ═══ parse ═════════════════════════════════════════════════════ */

    /**
     * Parse a DER certificate into a fully decoded object. Never throws for a
     * merely unusual certificate — only for something that is not a certificate.
     */
    function parse(der) {
        var bytes = Asn1.u8(der);
        var node = Asn1.parse(bytes);
        if (node.tag !== TAG.SEQUENCE || !node.children || node.children.length < 3) {
            throw new Error('CERT_NOT_SEQUENCE');
        }
        var tbs = node.children[0];
        var sigAlgNode = node.children[1];
        var sigValue = node.children[2];
        if (tbs.tag !== TAG.SEQUENCE) throw new Error('CERT_NO_TBS');

        var k = tbs.children || [];
        var idx = 0, version = 1;
        if (k[0] && k[0].cls === 2 && k[0].tagNumber === 0) {
            version = intOf(k[0].children[0]) + 1;
            idx = 1;
        }

        var serialNode = k[idx++];
        var innerSigAlg = k[idx++];
        var issuerNode = k[idx++];
        var validityNode = k[idx++];
        var subjectNode = k[idx++];
        var spkiNode = k[idx++];

        if (!spkiNode) throw new Error('CERT_TRUNCATED_TBS');

        var extensions = [];
        for (var i = idx; i < k.length; i++) {
            if (k[i].cls === 2 && k[i].tagNumber === 3 && k[i].children && k[i].children[0]) {
                extensions = parseExtensions(k[i].children[0]);
            }
        }

        var sigOid = sigAlgNode.children && sigAlgNode.children[0] ? sigAlgNode.children[0].oid : null;
        var sigInfo = Oid.sigInfo(sigOid);
        if (sigOid === '1.2.840.113549.1.1.10') {
            var pss = readPssParams(sigAlgNode.children[1]);
            if (pss) { sigInfo = { label: 'RSASSA-PSS (' + pss.hash + ')', key: 'RSA', hash: pss.hash, pss: pss }; }
        }

        var notBefore = validityNode.children[0].date;
        var notAfter = validityNode.children[1].date;

        var publicKey = null, publicKeyError = null;
        try { publicKey = Keys.parsePublic(spkiNode.full); }
        catch (e) { publicKeyError = e.message; }

        var cert = {
            der: bytes,
            version: version,
            serialHex: normaliseSerial(serialNode.bytes),
            serialBytes: Asn1.u8(serialNode.bytes),
            serialBits: Keys.bitLength(serialNode.bytes),
            signatureAlgorithm: sigInfo.label,
            signatureAlgorithmOid: sigOid,
            signatureHash: sigInfo.hash,
            signatureKeyType: sigInfo.key,
            signatureValue: sigValue.bits || sigValue.bytes,
            issuer: parseName(issuerNode),
            subject: parseName(subjectNode),
            notBefore: notBefore,
            notAfter: notAfter,
            publicKey: publicKey,
            publicKeyError: publicKeyError,
            spki: spkiNode.full,
            extensions: extensions,
            tbsDer: tbs.full,
            // The inner and outer signature algorithms must agree; a mismatch is
            // a (very old) signature-substitution vector and worth surfacing.
            algorithmMismatch: !!(innerSigAlg && innerSigAlg.children && innerSigAlg.children[0] &&
                                  innerSigAlg.children[0].oid !== sigOid)
        };

        decorateCert(cert);
        return cert;
    }

    function normaliseSerial(bytes) {
        var hex = Asn1.toHex(bytes).replace(/^00/, '');
        return hex.length ? hex : '00';
    }

    function readPssParams(params) {
        if (!params || !params.children) return null;
        var hash = 'SHA-1', saltLen = 20;
        params.children.forEach(function (c) {
            if (c.tagNumber === 0 && c.children && c.children[0] && c.children[0].children) {
                hash = Oid.HASH[c.children[0].children[0].oid] || hash;
            } else if (c.tagNumber === 2 && c.children && c.children[0]) {
                saltLen = intOf(c.children[0]);
            }
        });
        return { hash: hash, saltLength: saltLen };
    }

    /** Attach the derived views every consumer needs, computed once. */
    function decorateCert(cert) {
        var san = ext(cert, '2.5.29.17');
        cert.sanEntries = san && Array.isArray(san.value) ? san.value : [];
        cert.dnsNames = cert.sanEntries.filter(function (e) { return e.kind === 'dns'; })
            .map(function (e) { return e.value; });
        cert.ipAddresses = cert.sanEntries.filter(function (e) { return e.kind === 'ip'; })
            .map(function (e) { return e.value; });
        cert.emails = cert.sanEntries.filter(function (e) { return e.kind === 'email'; })
            .map(function (e) { return e.value; });

        var bc = ext(cert, '2.5.29.19');
        cert.isCa = !!(bc && bc.value && bc.value.ca);
        cert.pathLen = bc && bc.value ? bc.value.pathLen : null;
        cert.hasBasicConstraints = !!bc;

        var ku = ext(cert, '2.5.29.15');
        cert.keyUsage = ku && Array.isArray(ku.value) ? ku.value : [];

        var eku = ext(cert, '2.5.29.37');
        cert.extKeyUsage = eku && Array.isArray(eku.value) ? eku.value : [];
        cert.isServerAuth = cert.extKeyUsage.some(function (e) {
            return e.oid === '1.3.6.1.5.5.7.3.1' || e.oid === '2.5.29.37.0';
        });

        var ski = ext(cert, '2.5.29.14');
        cert.subjectKeyId = ski ? ski.value : null;
        var aki = ext(cert, '2.5.29.35');
        cert.authorityKeyId = aki && aki.value ? aki.value.keyId : null;

        var aia = ext(cert, '1.3.6.1.5.5.7.1.1');
        cert.ocspUrls = [];
        cert.caIssuerUrls = [];
        if (aia && Array.isArray(aia.value)) {
            aia.value.forEach(function (a) {
                if (a.method === '1.3.6.1.5.5.7.48.1') cert.ocspUrls.push(a.location);
                else if (a.method === '1.3.6.1.5.5.7.48.2') cert.caIssuerUrls.push(a.location);
            });
        }

        var crl = ext(cert, '2.5.29.31');
        cert.crlUrls = crl && Array.isArray(crl.value) ? crl.value : [];

        var pol = ext(cert, '2.5.29.32');
        cert.policies = pol && Array.isArray(pol.value) ? pol.value : [];
        cert.validationLevel = null;
        cert.policies.forEach(function (p) { if (p.level && !cert.validationLevel) cert.validationLevel = p.level; });

        var tlsf = ext(cert, '1.3.6.1.5.5.7.1.24');
        cert.mustStaple = !!(tlsf && tlsf.value && tlsf.value.mustStaple);

        var sct = ext(cert, '1.3.6.1.4.1.11129.2.4.2');
        cert.scts = sct && Array.isArray(sct.value) ? sct.value : [];

        cert.selfIssued = cert.subject.rfc4514 === cert.issuer.rfc4514;

        // Validity in days, and the "days left" everyone actually looks for.
        // Computed against a caller-suppliable clock in analyze.js; here we use
        // the real one only for the convenience fields.
        var now = Date.now();
        cert.validityDays = cert.notBefore && cert.notAfter
            ? Math.round((cert.notAfter - cert.notBefore) / 86400000) : null;
        cert.daysRemaining = cert.notAfter ? Math.floor((cert.notAfter - now) / 86400000) : null;
        cert.expired = !!(cert.notAfter && cert.notAfter.getTime() < now);
        cert.notYetValid = !!(cert.notBefore && cert.notBefore.getTime() > now);
    }

    function ext(cert, oid) {
        for (var i = 0; i < cert.extensions.length; i++) {
            if (cert.extensions[i].oid === oid) return cert.extensions[i];
        }
        return null;
    }

    /* ═══ hostname matching ═════════════════════════════════════════ */

    /**
     * RFC 6125 hostname matching, as browsers actually implement it:
     *  - only SAN is consulted (Chrome dropped CN fallback in 58, and every
     *    other browser followed) — so a CN-only cert matches NOTHING here,
     *    which is the correct and useful answer
     *  - a wildcard matches exactly one label, and only in the leftmost position
     */
    /**
     * A certificate can only hold A-labels: "пример.com" lives in the SAN as
     * "xn--80ak6aa92e.com". A person types the name they own, in their own
     * script — so comparing the two as raw strings answers "this certificate
     * does not cover your domain" about a certificate that covers it exactly.
     * A confident wrong answer, and on the market this site mostly serves.
     *
     * Browsers convert to A-labels before matching, and so must we. The URL
     * parser is the browser's own IDNA implementation — no table to ship, no
     * table to let rot. Falls back to the input unchanged where it is absent
     * (Node without ICU), which is exactly today's behaviour.
     */
    function toAscii(host) {
        if (!/[^\x00-\x7F]/.test(host)) return host;      // already an A-label
        try {
            var u = new URL('https://' + host);
            return (u.hostname || host).toLowerCase();
        } catch (e) { return host; }
    }

    function matchesHostname(cert, hostname) {
        var host = String(hostname || '').toLowerCase().replace(/\.$/, '');
        if (!host) return { matched: false, reason: 'empty' };
        host = toAscii(host);

        if (isIpLiteral(host)) {
            var hit = cert.ipAddresses.some(function (ip) { return ip.toLowerCase() === host; });
            return { matched: hit, via: hit ? 'ip' : null, reason: hit ? null : 'no-ip-san' };
        }

        for (var i = 0; i < cert.dnsNames.length; i++) {
            var name = String(cert.dnsNames[i]).toLowerCase().replace(/\.$/, '');
            if (name === host) return { matched: true, via: 'exact', name: name };
            if (name.indexOf('*.') === 0) {
                var suffix = name.slice(1); // ".example.com"
                if (host.length > suffix.length && host.slice(-suffix.length) === suffix) {
                    var left = host.slice(0, host.length - suffix.length);
                    // One label only: "a.b.example.com" does NOT match "*.example.com".
                    if (left.indexOf('.') < 0) return { matched: true, via: 'wildcard', name: name };
                }
            }
        }
        var cn = cert.subject.get('CN');
        if (cn && cn.toLowerCase() === host) {
            return { matched: false, reason: 'cn-only', cn: cn };
        }
        return { matched: false, reason: cert.dnsNames.length ? 'no-match' : 'no-san' };
    }

    function isIpLiteral(s) {
        return /^\d{1,3}(\.\d{1,3}){3}$/.test(s) || s.indexOf(':') >= 0;
    }

    /* ═══ signature verification ════════════════════════════════════ */

    var WC_HASH = { 'SHA-1': 'SHA-1', 'SHA-224': null, 'SHA-256': 'SHA-256', 'SHA-384': 'SHA-384', 'SHA-512': 'SHA-512' };

    /**
     * Verify `cert` was signed by the key in `issuerSpki`.
     * Returns {valid, reason} — never throws for an unsupported algorithm, so
     * a GOST or SHA-224 chain degrades to "cannot check" instead of "invalid",
     * which are very different things to tell a user.
     */
    async function verifySignature(cert, issuerSpki) {
        var hash = WC_HASH[cert.signatureHash];
        if (!hash && cert.signatureKeyType !== 'Ed25519') {
            return { valid: null, reason: 'UNSUPPORTED_HASH:' + cert.signatureHash };
        }
        var issuerKey;
        try { issuerKey = Keys.parsePublic(issuerSpki); }
        catch (e) { return { valid: null, reason: 'BAD_ISSUER_KEY' }; }

        try {
            var subtle = Keys.subtle();
            var algo, sig = Asn1.u8(cert.signatureValue);

            if (cert.signatureKeyType === 'RSA') {
                var isPss = cert.signatureAlgorithmOid === '1.2.840.113549.1.1.10';
                algo = isPss
                    ? { name: 'RSA-PSS', saltLength: hashLenBytes(hash) }
                    : { name: 'RSASSA-PKCS1-v1_5' };
                var impAlgo = isPss
                    ? { name: 'RSA-PSS', hash: hash }
                    : { name: 'RSASSA-PKCS1-v1_5', hash: hash };
                var rk = await subtle.importKey('spki', Keys.bufferOf(issuerKey.spki), impAlgo, false, ['verify']);
                return { valid: await subtle.verify(algo, rk, Keys.bufferOf(sig), Keys.bufferOf(cert.tbsDer)) };
            }

            if (cert.signatureKeyType === 'EC') {
                if (!issuerKey.webcryptoCurve) return { valid: null, reason: 'UNSUPPORTED_CURVE:' + issuerKey.curve };
                var ek = await subtle.importKey('spki', Keys.bufferOf(issuerKey.spki),
                    { name: 'ECDSA', namedCurve: issuerKey.webcryptoCurve }, false, ['verify']);
                // X.509 stores ECDSA signatures as DER SEQUENCE{r,s}; WebCrypto
                // wants raw r||s. Missing this conversion is why hand-rolled
                // verifiers report every ECDSA chain as broken.
                var raw = ecdsaDerToRaw(sig, curveByteLen(issuerKey.webcryptoCurve));
                if (!raw) return { valid: null, reason: 'BAD_ECDSA_SIG' };
                return { valid: await subtle.verify({ name: 'ECDSA', hash: hash }, ek,
                    Keys.bufferOf(raw), Keys.bufferOf(cert.tbsDer)) };
            }

            if (cert.signatureKeyType === 'Ed25519') {
                var edk = await subtle.importKey('spki', Keys.bufferOf(issuerKey.spki),
                    { name: 'Ed25519' }, false, ['verify']);
                return { valid: await subtle.verify({ name: 'Ed25519' }, edk,
                    Keys.bufferOf(sig), Keys.bufferOf(cert.tbsDer)) };
            }

            return { valid: null, reason: 'UNSUPPORTED_ALGORITHM:' + cert.signatureAlgorithm };
        } catch (e) {
            if (String(e.message) === 'WEBCRYPTO_UNAVAILABLE') return { valid: null, reason: 'WEBCRYPTO_UNAVAILABLE' };
            return { valid: null, reason: 'VERIFY_ERROR:' + e.message };
        }
    }

    function hashLenBytes(hash) {
        return { 'SHA-1': 20, 'SHA-256': 32, 'SHA-384': 48, 'SHA-512': 64 }[hash] || 32;
    }

    function curveByteLen(name) {
        return { 'P-256': 32, 'P-384': 48, 'P-521': 66 }[name] || 32;
    }

    function ecdsaDerToRaw(der, size) {
        try {
            var node = Asn1.parse(der);
            if (!node.children || node.children.length !== 2) return null;
            var out = new Uint8Array(size * 2);
            [0, 1].forEach(function (i) {
                var v = node.children[i].bytes, s = 0;
                while (s < v.length - 1 && v[s] === 0) s++;   // strip DER sign pad
                var val = v.subarray(s);
                out.set(val, size * (i + 1) - val.length);     // left-pad to fixed width
            });
            return out;
        } catch (e) { return null; }
    }

    function ecdsaRawToDer(raw) {
        var half = raw.length / 2;
        return Asn1.sequence(
            Asn1.integer(raw.subarray(0, half)),
            Asn1.integer(raw.subarray(half))
        );
    }

    /* ═══ building ══════════════════════════════════════════════════ */

    /**
     * Build and sign a certificate.
     * opts: {subject:[{short,value}], issuer, serial, notBefore, notAfter,
     *        publicKeySpki, signerKey (CryptoKey), signerKind, hash,
     *        extensions:{san, basicConstraints, keyUsage, extKeyUsage, ski, aki}}
     */
    async function build(opts) {
        var subtle = Keys.subtle();
        var hash = opts.hash || 'SHA-256';
        var sigOid = Oid.sigOidFor(opts.signerKind, hash);
        if (opts.signerKind === 'Ed25519') sigOid = '1.3.101.112';
        if (!sigOid) throw new Error('no signature OID for ' + opts.signerKind + '/' + hash);

        // Ed25519 AlgorithmIdentifier has absent parameters; RSA has explicit
        // NULL; ECDSA omits them. Getting this wrong yields a cert that openssl
        // parses but Java rejects.
        var sigAlgId = opts.signerKind === 'RSA'
            ? Asn1.sequence(Asn1.oid(sigOid), Asn1.nullValue())
            : Asn1.sequence(Asn1.oid(sigOid));

        var serial = opts.serial || Keys.randomBytes(16);
        // CA/B Forum requires >= 64 bits of entropy; also clear the top bit so
        // the DER INTEGER stays positive without a pad byte.
        serial = Asn1.u8(serial).slice();
        serial[0] &= 0x7f;
        if (serial[0] === 0) serial[0] = 0x01;

        var extNodes = buildExtensions(opts.extensions || {});

        var tbsParts = [
            Asn1.explicit(0, Asn1.integerFromNumber((opts.version || 3) - 1)),
            Asn1.integer(serial),
            sigAlgId,
            buildName(opts.issuer),
            Asn1.sequence(Asn1.time(opts.notBefore), Asn1.time(opts.notAfter)),
            buildName(opts.subject),
            Asn1.u8(opts.publicKeySpki)
        ];
        if (extNodes.length) tbsParts.push(Asn1.explicit(3, Asn1.sequence(extNodes)));

        var tbs = Asn1.sequence(tbsParts);
        var signature = await signBytes(subtle, opts.signerKey, opts.signerKind, hash, tbs, opts.signerCurve);

        return Asn1.sequence(tbs, sigAlgId, Asn1.bitString(signature, 0));
    }

    async function signBytes(subtle, key, kind, hash, data, curve) {
        var algo;
        if (kind === 'RSA') algo = { name: 'RSASSA-PKCS1-v1_5' };
        else if (kind === 'EC') algo = { name: 'ECDSA', hash: hash };
        else if (kind === 'Ed25519') algo = { name: 'Ed25519' };
        else throw new Error('cannot sign with ' + kind);

        var sig = new Uint8Array(await subtle.sign(algo, key, Keys.bufferOf(data)));
        // WebCrypto returns raw r||s for ECDSA; X.509 wants the DER form.
        if (kind === 'EC') return ecdsaRawToDer(sig);
        return sig;
    }

    function buildExtensions(spec) {
        var out = [];
        function add(oid, critical, valueDer) {
            var parts = [Asn1.oid(oid)];
            if (critical) parts.push(Asn1.boolean(true));
            parts.push(Asn1.octetString(valueDer));
            out.push(Asn1.sequence(parts));
        }

        if (spec.basicConstraints) {
            var bcParts = [];
            if (spec.basicConstraints.ca) bcParts.push(Asn1.boolean(true));
            if (spec.basicConstraints.pathLen !== undefined && spec.basicConstraints.pathLen !== null) {
                bcParts.push(Asn1.integerFromNumber(spec.basicConstraints.pathLen));
            }
            add('2.5.29.19', true, Asn1.sequence(bcParts));
        }
        if (spec.keyUsage && spec.keyUsage.length) {
            var idxs = spec.keyUsage.map(function (u) { return KEY_USAGE_BITS.indexOf(u); })
                .filter(function (i) { return i >= 0; });
            add('2.5.29.15', true, Asn1.namedBits(idxs));
        }
        if (spec.extKeyUsage && spec.extKeyUsage.length) {
            add('2.5.29.37', false, Asn1.sequence(spec.extKeyUsage.map(function (o) { return Asn1.oid(o); })));
        }
        if (spec.san && spec.san.length) {
            // SAN is critical only when the subject DN is empty (RFC 5280).
            add('2.5.29.17', !!spec.sanCritical, Asn1.sequence(spec.san.map(buildGeneralName)));
        }
        if (spec.subjectKeyId) {
            add('2.5.29.14', false, Asn1.octetString(spec.subjectKeyId));
        }
        if (spec.authorityKeyId) {
            add('2.5.29.35', false, Asn1.sequence(Asn1.context(0, spec.authorityKeyId, false)));
        }
        if (spec.crlUrls && spec.crlUrls.length) {
            add('2.5.29.31', false, Asn1.sequence(spec.crlUrls.map(function (u) {
                return Asn1.sequence(Asn1.explicit(0, Asn1.context(0,
                    Asn1.context(6, new TextEncoder().encode(u), false))));
            })));
        }
        return out;
    }

    /** SHA-1 of the public key BIT STRING — the conventional SKI (RFC 5280 §4.2.1.2 method 1). */
    async function subjectKeyIdFor(spki) {
        var node = Asn1.parse(spki);
        var bitStr = node.children[1];
        return await Keys.digest('SHA-1', bitStr.bits);
    }

    var API = {
        parse: parse, parseName: parseName, buildName: buildName,
        parseGeneralNames: parseGeneralNames, buildGeneralName: buildGeneralName,
        parseExtensions: parseExtensions, decodeExtension: decodeExtension,
        matchesHostname: matchesHostname, verifySignature: verifySignature,
        build: build, buildExtensions: buildExtensions, subjectKeyIdFor: subjectKeyIdFor,
        signBytes: signBytes, ecdsaDerToRaw: ecdsaDerToRaw, ecdsaRawToDer: ecdsaRawToDer,
        ext: ext, parseIpToBytes: parseIpToBytes, formatIp: formatIp,
        KEY_USAGE_BITS: KEY_USAGE_BITS, shortToOid: shortToOid
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.X509 = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
